import {
  MoveObservationMediaResult,
  CreateCalendarEventFromEventContextInput,
  CreateMessageDraftActionResult,
  CalendarEventWriteBackActionResult,
  CreateCalendarEventFromScratchActionResult,
  CreateCalendarEventFromScratchInput,
  CreateMessageComposeDraftInput,
  CreateMessageReplyDraftInput,
  DiscardMessageDraftActionResult,
  SendMessageDraftActionResult,
  UpdateMessageDraftActionResult,
  UpdateMessageDraftInput,
  UpdateCalendarEventFromEventContextInput,
  DocumentDeleteSubtreeResult,
  DocumentHierarchyMutationResult,
  DocumentRender,
  DeletionCommitInput,
  DeletionCommitResult,
  DeletionImpact,
  DeletionPreviewInput,
  Content,
  ContentActionResult,
  ContentCreate,
  MessageExtractionRunResult,
  MyObligationPriorityItem,
  ObligationArcResponse,
  ContentListQueryInput,
  ContentListResult,
  ContentUpdate,
  Tracking,
  TrackingUpdate,
  MoveDocumentTableImageInput,
} from '@vivport/backend/types';
import type { ContentCreateExtras } from '@vivport/backend/types';
import { httpClient } from './http-client';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type ContentCreateInput = DistributiveOmit<ContentCreate, 'memberId'> & {
  memberId?: number;
  file?: Blob | File;
};

export type ObservationGroupInput = { start: true } | { groupId: number };

export type MoveObservationMediaInput = {
  contentId: number;
  /** Omitted splits the item out into a new Observation under the same apex. */
  toObservationGroupId?: number;
};



export type CreateDocumentChildInput = {
  parentDocumentId: number;
  title?: string;
  description?: string;
  sortOrder?: number;
};

export type CreateDocumentSiblingInput = {
  siblingDocumentId: number;
  title?: string;
  description?: string;
  sortOrder?: number;
};

export type MoveDocumentNodeInput = {
  documentId: number;
  targetParentDocumentId: number;
  sortOrder?: number;
};

/**
 * Content API
 * Type-safe wrappers for all content management endpoints
 * @requires Authentication
 */

// ============================================================================
// Content CRUD Operations
// ============================================================================
/**
 * List content with filtering and pagination
 */
export async function listContent(params?: ContentListQueryInput): Promise<ContentListResult> {
  return httpClient.get<ContentListResult>('/protected/content/', { params });
}

/**
 * Get content detail by ID
 */
export async function getContent(id: number | string): Promise<Content> {
  return httpClient.get<Content>(`/protected/content/${encodeURIComponent(String(id))}`);
}

/**
 * Manual extraction rig: classify one message's obligations into MessageExtracts.
 */
export async function extractMessageObligations(contentId: number): Promise<MessageExtractionRunResult> {
  return httpClient.post<MessageExtractionRunResult>(`/protected/content/${contentId}/extract`, {});
}

/**
 * The calling member's personal priority (latest-wins) + shared computedScore per extract.
 */
export async function fetchMyObligationPriorities(
  extractIds: number[]
): Promise<{ items: MyObligationPriorityItem[] }> {
  return httpClient.get<{ items: MyObligationPriorityItem[] }>(
    `/protected/content/obligation-priorities?ids=${extractIds.join(',')}`
  );
}

/**
 * The obligation's read-only arc: ordered transition timeline + backend-computed metrics.
 */
export async function fetchObligationArc(extractId: number): Promise<ObligationArcResponse> {
  return httpClient.get<ObligationArcResponse>(`/protected/content/${extractId}/arc`);
}

/**
 * Create new content
 * @note Supports file uploads for photos/recordings
 */
export async function createContent(data: ContentCreateInput): Promise<Content & ContentCreateExtras> {
  // Handle file uploads
  if ('file' in data && data.file) {
    const formData = new FormData();
    
    // Add the file
    const uploadFile = data.file;
    if (!(uploadFile instanceof Blob)) {
      throw new TypeError('Invalid upload payload: expected file to be a Blob or File.');
    }
    let uploadFieldName: 'photo' | 'recording' | 'video' | 'file';
    if (data.contentType === 'PHOTO') {
      uploadFieldName = 'photo';
    } else if (data.contentType === 'RECORDING') {
      uploadFieldName = 'recording';
    } else if (data.contentType === 'VIDEO') {
      uploadFieldName = 'video';
    } else if (data.contentType === 'FILE') {
      uploadFieldName = 'file';
    } else {
      throw new TypeError(`Unsupported multipart upload contentType: ${data.contentType}`);
    }
    formData.append(uploadFieldName, uploadFile);
    
    // Add contentType to FormData body (backend needs it in body for Zod validation, even though it's also in query string)
    formData.append('contentType', data.contentType);
    
    // Add other fields (excluding file and memberId which backend sets automatically)
    Object.entries(data).forEach(([key, value]) => {
      if (key !== 'file' && key !== 'contentType' && key !== 'memberId' && value !== undefined) {
        // For arrays, we need to handle them specially
        if (Array.isArray(value)) {
          // FormData arrays: append each item with same key
          value.forEach((item) => {
            formData.append(key, String(item));
          });
        } else {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      }
    });

    // Send contentType as query parameter for multipart requests (backend middleware expects it)
    return httpClient.post<Content & ContentCreateExtras, FormData>(`/protected/content/?contentType=${data.contentType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Regular JSON request
  return httpClient.post<Content & ContentCreateExtras, ContentCreateInput>('/protected/content/', data);
}

/**
 * Update content
 */
export async function updateContent(id: number, data: ContentUpdate): Promise<Content> {
  return httpClient.patch<Content, ContentUpdate>(`/protected/content/${id}`, data);
}

// ============================================================================
// Content Actions
// ============================================================================

/**
 * Execute content-specific action
 * @param id Content ID
 * @param actionName Action name (e.g., 'regenerate', 'connectAnchor')
 * @param params Action-specific parameters
 */
export async function executeContentAction(
  id: number,
  actionName: string,
  params?: Record<string, any>
): Promise<ContentActionResult> {
  return httpClient.post<ContentActionResult>(`/protected/content/${id}/${actionName}`, params);
}

/**
 * Regenerate content (convenience wrapper)
 */
export async function regenerateContent(id: number, awaitLLM: boolean = true): Promise<ContentActionResult> {
  return executeContentAction(id, 'regenerate', { awaitLLM });
}

export async function createMessageReplyDraft(
  sourceContentId: number,
  input: CreateMessageReplyDraftInput
): Promise<CreateMessageDraftActionResult> {
  return httpClient.post<CreateMessageDraftActionResult, CreateMessageReplyDraftInput>(
    `/protected/content/${sourceContentId}/createMessageReplyDraft`,
    input
  );
}

export async function composeMessageDraft(
  input: CreateMessageComposeDraftInput
): Promise<CreateMessageDraftActionResult> {
  return httpClient.post<CreateMessageDraftActionResult, CreateMessageComposeDraftInput>(
    '/protected/content/messages/compose',
    input
  );
}

export async function createCalendarEventFromScratch(
  input: CreateCalendarEventFromScratchInput
): Promise<CreateCalendarEventFromScratchActionResult> {
  return httpClient.post<CreateCalendarEventFromScratchActionResult, CreateCalendarEventFromScratchInput>(
    '/protected/content/events/create',
    input
  );
}

export async function moveObservationMedia(
  input: MoveObservationMediaInput
): Promise<MoveObservationMediaResult> {
  return httpClient.post<MoveObservationMediaResult, MoveObservationMediaInput>(
    '/protected/content/observations/move',
    input
  );
}

export async function sendMessageDraft(draftContentId: number): Promise<SendMessageDraftActionResult> {
  return httpClient.post<SendMessageDraftActionResult, Record<string, never>>(
    `/protected/content/${draftContentId}/sendMessageDraft`,
    {}
  );
}

export async function updateMessageDraft(
  draftContentId: number,
  input: UpdateMessageDraftInput
): Promise<UpdateMessageDraftActionResult> {
  return httpClient.post<UpdateMessageDraftActionResult, UpdateMessageDraftInput>(
    `/protected/content/${draftContentId}/updateMessageDraft`,
    input
  );
}

export async function discardMessageDraft(draftContentId: number): Promise<DiscardMessageDraftActionResult> {
  return httpClient.post<DiscardMessageDraftActionResult, Record<string, never>>(
    `/protected/content/${draftContentId}/discardMessageDraft`,
    {}
  );
}

export async function createCalendarEventFromEventContext(
  sourceContentId: number,
  input: CreateCalendarEventFromEventContextInput
): Promise<CalendarEventWriteBackActionResult> {
  return httpClient.post<CalendarEventWriteBackActionResult, CreateCalendarEventFromEventContextInput>(
    `/protected/content/${sourceContentId}/createCalendarEventFromEventContext`,
    input
  );
}

export async function updateCalendarEventFromEventContext(
  sourceContentId: number,
  input: UpdateCalendarEventFromEventContextInput
): Promise<CalendarEventWriteBackActionResult> {
  return httpClient.post<CalendarEventWriteBackActionResult, UpdateCalendarEventFromEventContextInput>(
    `/protected/content/${sourceContentId}/updateCalendarEventFromEventContext`,
    input
  );
}

// ============================================================================
// Bulk Operations
// ============================================================================

/**
 * Bulk delete content items
 */
export async function bulkDeleteContent(input: DeletionCommitInput): Promise<DeletionCommitResult> {
  return httpClient.post<DeletionCommitResult, DeletionCommitInput>('/protected/content/bulk-delete', input);
}

/**
 * Delete a plain set of ids: preview to obtain the plan hash the commit requires,
 * then commit that exact plan. Callers that let the member choose optional extras
 * should preview themselves and call bulkDeleteContent with the chosen ids.
 */
export async function deleteContentAfterPreview(roots: number[]): Promise<DeletionCommitResult> {
  const impact = await previewContentDeletion({ roots });
  return bulkDeleteContent({ roots, optionalIncluded: [], planHash: impact.planHash });
}

/**
 * Preview what deleting a set of content items would remove. Changes nothing.
 */
export async function previewContentDeletion(input: DeletionPreviewInput): Promise<DeletionImpact> {
  return httpClient.post<DeletionImpact, DeletionPreviewInput>('/protected/content/delete-preview', input);
}

export async function createDocumentChild(input: CreateDocumentChildInput): Promise<DocumentHierarchyMutationResult> {
  return httpClient.post<DocumentHierarchyMutationResult, CreateDocumentChildInput>(
    '/protected/content/document/create-child',
    input
  );
}

export async function createDocumentSibling(input: CreateDocumentSiblingInput): Promise<DocumentHierarchyMutationResult> {
  return httpClient.post<DocumentHierarchyMutationResult, CreateDocumentSiblingInput>(
    '/protected/content/document/create-sibling',
    input
  );
}

export async function moveDocumentNode(input: MoveDocumentNodeInput): Promise<DocumentHierarchyMutationResult> {
  return httpClient.post<DocumentHierarchyMutationResult, MoveDocumentNodeInput>(
    '/protected/content/document/move-node',
    input
  );
}

export async function moveDocumentTableImage(input: MoveDocumentTableImageInput): Promise<Content> {
  return httpClient.post<Content, MoveDocumentTableImageInput>(
    '/protected/content/document/move-table-image',
    input
  );
}

export async function deleteDocumentSubtree(documentId: number): Promise<DocumentDeleteSubtreeResult> {
  return httpClient.delete<DocumentDeleteSubtreeResult>(`/protected/content/document/delete-subtree/${documentId}`);
}

// ============================================================================
// Content Tracking
// ============================================================================

/**
 * Update content tracking
 */
export async function updateContentTracking(contentId: number, tracking: TrackingUpdate): Promise<Tracking> {
  return httpClient.patch<Tracking, TrackingUpdate>(`/protected/content/${contentId}/tracking`, tracking);
}

/**
 * Mark content as read
 */
export async function markAsRead(contentId: number): Promise<Tracking> {
  return updateContentTracking(contentId, { isRead: true });
}

/**
 * Mark content as unread
 */
export async function markAsUnread(contentId: number): Promise<Tracking> {
  return updateContentTracking(contentId, { isRead: false });
}

/**
 * Set read/unread state for content
 */
export async function setReadState(contentId: number, isRead: boolean): Promise<Tracking> {
  return updateContentTracking(contentId, { isRead });
}

/**
 * Toggle star on content
 */
export async function toggleStar(contentId: number, isStarred: boolean): Promise<Tracking> {
  return updateContentTracking(contentId, { isStarred });
}

export type DocumentExportFormat = 'pdf' | 'docx' | 'markdown';

const EXPORT_EXTENSION: Record<DocumentExportFormat, string> = {
  pdf: 'pdf',
  docx: 'docx',
  markdown: 'md',
};

export async function downloadDocumentExport(
  documentRef: number | string,
  format: DocumentExportFormat,
  filename?: string,
): Promise<void> {
  const url = `/protected/content/document/${encodeURIComponent(String(documentRef))}/${format}`;
  const blob = await httpClient.get<Blob>(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = objectUrl;
  a.download = filename ? `${filename}.${EXPORT_EXTENSION[format]}` : '';
  window.document.body.appendChild(a);
  a.click();
  window.document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export async function downloadContentSelectionExport(
  contentIds: number[],
  filename = 'vivport-export.zip',
): Promise<void> {
  const blob = await httpClient.post<Blob>(
    '/protected/content/export',
    { contentIds },
    { responseType: 'blob' },
  );
  const objectUrl = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  window.document.body.appendChild(a);
  a.click();
  window.document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export async function getDocumentImageBlob(code: string): Promise<Blob> {
  return httpClient.get<Blob>(
    `/protected/content/document-image/${encodeURIComponent(code)}`,
    { responseType: 'blob' },
  );
}

export async function getDocumentRender(documentRef: number | string): Promise<DocumentRender> {
  return httpClient.get<DocumentRender>(
    `/protected/content/document/${encodeURIComponent(String(documentRef))}/render`,
  );
}

